import type { ReactNode } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CohortGrid,
  DataTable,
  Trend,
  type ColumnDef,
} from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { QuietCompany, RetentionView } from '../types';
import { cohortRows, weekHeadings, weeklyPoints, weeksWord } from './retention-rows';
import '../admin.css';

export interface RetentionBoardProps {
  retention: RetentionView;
  /** The router's link, so every quiet company opens its page. */
  renderLink: (href: string, children: ReactNode) => ReactNode;
}

/** A week's Monday as a date; noon UTC so the calendar day holds in every time zone. */
function weekLabel(f: Formatter): (week: string) => string {
  return (week) => f.date(`${week}T12:00:00Z`);
}

function columnsFor(
  f: Formatter,
  renderLink: RetentionBoardProps['renderLink'],
): ColumnDef<QuietCompany, unknown>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Company',
      cell: (c) => renderLink(`/admin/customers/${c.row.original.tenant}`, c.row.original.name),
    },
    {
      accessorKey: 'lastSeenAt',
      header: 'Last seen',
      cell: (c) => f.ago(c.row.original.lastSeenAt),
    },
    {
      accessorKey: 'weeksActive',
      header: 'Was active for',
      cell: (c) => weeksWord(c.row.original.weeksActive),
    },
  ];
}

function Cohorts({ retention, f }: { retention: RetentionView; f: Formatter }): ReactNode {
  const rows = cohortRows(retention.cohorts, retention.weeks, weekLabel(f));
  const anyone = retention.cohorts.some((c) => c.size > 0);
  return (
    <Card className="ad-retention__cohorts">
      <CardHeader>
        <CardTitle>Still active, by weeks since sign-up</CardTitle>
      </CardHeader>
      <p className="ad-small">
        Each row is a sign-up week; each cell the share of those companies that did anything in that
        week after. Blank where the week has not come yet.
      </p>
      {anyone ? (
        <CohortGrid
          rows={rows}
          periods={weekHeadings(retention.weeks)}
          label="Share of each sign-up cohort active in each week since"
        />
      ) : (
        <p className="ad-small">No company signed up in the last {weeksWord(retention.weeks)}.</p>
      )}
    </Card>
  );
}

function Weekly({
  retention,
  f,
  what,
}: {
  retention: RetentionView;
  f: Formatter;
  what: 'companies' | 'people';
}): ReactNode {
  const points = weeklyPoints(retention.weekly, what, weekLabel(f));
  const latest = points.at(-1)?.value ?? 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Active {what} each week</CardTitle>
      </CardHeader>
      <p className="ad-small">
        <strong>{f.int(latest)}</strong> this week; the same weeks {weeksWord(retention.weeks)}{' '}
        earlier dashed behind.
      </p>
      {points.length > 0 ? (
        <Trend
          points={points}
          label={`Active ${what} each week, this period against the one before`}
          format={f.int}
        />
      ) : (
        <p className="ad-small">No weeks to show yet.</p>
      )}
    </Card>
  );
}

function Quiet({
  retention,
  f,
  renderLink,
}: {
  retention: RetentionView;
  f: Formatter;
  renderLink: RetentionBoardProps['renderLink'];
}): ReactNode {
  return (
    <Card className="ad-retention__quiet">
      <CardHeader>
        <CardTitle>Went quiet this week</CardTitle>
      </CardHeader>
      <p className="ad-small">
        Companies that did something in the four weeks before, and nothing in the last seven days.
      </p>
      <DataTable
        columns={columnsFor(f, renderLink)}
        data={retention.quiet}
        dense
        caption="Companies that went quiet"
        getRowId={(r) => r.tenant}
        empty="Nobody went quiet this week."
      />
    </Card>
  );
}

/**
 * What "active" means here, until retention is measured on pages (round 3, marketing must 3), so no
 * deck number is built on page views by mistake.
 */
export const retentionActiveHelp =
  'Active means any visit to Paige; retention on processed pages comes with the volume rollup.';

/** The cohort grid, the two weekly lines, and who went quiet; every company opens its page. */
export function RetentionBoard({ retention, renderLink }: RetentionBoardProps): ReactNode {
  const f = useFormat();
  return (
    <div className="ad-retention">
      <p className="ad-small ad-retention__help">{retentionActiveHelp}</p>
      <Cohorts retention={retention} f={f} />
      <Weekly retention={retention} f={f} what="companies" />
      <Weekly retention={retention} f={f} what="people" />
      <Quiet retention={retention} f={f} renderLink={renderLink} />
    </div>
  );
}
